<template>
	<div class="flex">
		<button
			class="w-full h-full py-3 min-h-[50px] text-white md:text-base text-[15px] px-3 rounded-4xl transition-colors ease-in-out duration-200"
			:class="[
				`${stockStatus?.buttonColor ? stockStatus.buttonColor : 'bg-green-light hover:bg-green-600'}`,
				`${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`
			]"
			data-testid="add-to-cart-button"
			:disabled="(isPending && cartStore.updating) || disabled"
			@click="handleAddToCart"
		>
			<div v-if="isPending && cartStore.updating">
				<div
					class="inline-block h-5 w-5 mr-4 animate-spin rounded-full border-4 border-solid border-current border-e-transparent align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite] dark:text-white"
					role="status"
				></div>
				<span>{{ $t("Adding") }}..</span>
			</div>
			<span v-else>{{ stockStatus?.buttonText || $t("Buy") }}</span>
		</button>
	</div>
</template>

<script setup lang="ts">
import type { ProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { storeToRefs } from "pinia";
import type { StockStatusItem } from "~/types/storyblok/storyblok";

interface Props {
	product: ProductItem;
	disabled?: boolean;
	stockStatus?: StockStatusItem;
}
const { product, disabled } = defineProps<Props>();

const cartStore = useCartStore();
// The shared quick-add: GA4 reporting, the deposit child line and the
// structure-product guard (a bundle is configured on its own page, never added
// blind). `ProductCardActions` already routes through it.
const { add } = useCatalogAddToCart();
const { $toast } = useNuxtApp();
const { t } = useI18n();

const { updating } = storeToRefs(cartStore);

const isPending = ref(false);

const handleAddToCart = async () => {
	isPending.value = true;

	try {
		await add(product);
	} catch (error) {
		// `add()` does not wrap `cartStore.addToCart`, which rethrows anything
		// but a stale basket — without this the spinner just stops.
		console.error("[catalog-add-to-cart] could not add the product", error);
		$toast.error(t("add_to_cart_failed"));
	} finally {
		isPending.value = false;
	}
};

watch(updating, () => {
	if (!cartStore.updating) {
		isPending.value = false;
	}
});
</script>
